import React, { useState } from 'react';
import { X, Plus, Trash, Image as ImageIcon } from 'lucide-react';
import { createChallenge } from '../../lib/store';
import { Challenge } from '../../types/challenges';
import { auth } from '../../lib/firebase';
import { sendPushNotification } from '../../lib/notifications';

interface NewChallengeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NewChallengeModal({ isOpen, onClose }: NewChallengeModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    difficulty: 'beginner' as Challenge['difficulty'],
    duration: 30,
    requirements: {
      flour: [''],
      equipment: [''],
      technique: ['']
    },
    steps: [{
      id: '1',
      title: '',
      description: '',
      tips: ['']
    }]
  });

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setSelectedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (!auth.currentUser) {
        throw new Error('You must be logged in to create a challenge');
      }

      if (!selectedImage) {
        throw new Error('Please upload a challenge image');
      }

      const startDate = new Date();
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + formData.duration);

      // Clean up empty arrays
      const cleanedFormData = {
        ...formData,
        requirements: {
          flour: formData.requirements.flour.filter(f => f.trim()),
          equipment: formData.requirements.equipment.filter(e => e.trim()),
          technique: formData.requirements.technique.filter(t => t.trim())
        },
        steps: formData.steps.map(step => ({
          ...step,
          tips: step.tips.filter(tip => tip.trim())
        }))
      };

      const challengeData = {
        ...cleanedFormData,
        startDate,
        endDate,
        createdBy: {
          userId: auth.currentUser.uid,
          name: auth.currentUser.displayName || 'Admin'
        },
        participants: [],
        isActive: true
      };

      await createChallenge(challengeData, selectedImage);
      
      try {
        await sendPushNotification({
          title: 'New Baking Challenge!',
          body: `Join "${formData.title}" - a ${formData.difficulty} level challenge`,
          data: {
            type: 'new_challenge',
            challengeTitle: formData.title
          }
        });
      } catch (notificationError) {
        console.warn('Failed to send notification:', notificationError);
      }

      onClose();
    } catch (err) {
      console.error('Error creating challenge:', err);
      setError('Failed to create challenge. Please ensure you have admin privileges and try again.');
    } finally {
      setLoading(false);
    }
  };

  const addArrayItem = (field: 'flour' | 'equipment' | 'technique') => {
    setFormData(prev => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        [field]: [...prev.requirements[field], '']
      }
    }));
  };

  const removeArrayItem = (field: 'flour' | 'equipment' | 'technique', index: number) => {
    setFormData(prev => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        [field]: prev.requirements[field].filter((_, i) => i !== index)
      }
    }));
  };

  const updateArrayItem = (
    field: 'flour' | 'equipment' | 'technique',
    index: number,
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      requirements: {
        ...prev.requirements,
        [field]: prev.requirements[field].map((item, i) => 
          i === index ? value : item
        )
      }
    }));
  };

  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      steps: [
        ...prev.steps,
        {
          id: (prev.steps.length + 1).toString(),
          title: '',
          description: '',
          tips: ['']
        }
      ]
    }));
  };

  const removeStep = (index: number) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  const updateStep = (index: number, field: keyof Challenge['steps'][0], value: any) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((step, i) =>
        i === index ? { ...step, [field]: value } : step
      )
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Create New Challenge</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-100 text-red-800 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Challenge Image
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-amber-500 transition-colors">
              <div className="space-y-1 text-center">
                {imagePreview ? (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Challenge preview"
                      className="mx-auto h-64 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedImage(null);
                        setImagePreview(null);
                      }}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="challenge-image"
                        className="relative cursor-pointer rounded-md font-medium text-amber-600 hover:text-amber-500"
                      >
                        <span>Upload a file</span>
                        <input
                          id="challenge-image"
                          name="challenge-image"
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={handleImageSelect}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, GIF up to 10MB
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Challenge Title
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg h-24"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Difficulty
              </label>
              <select
                value={formData.difficulty}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  difficulty: e.target.value as Challenge['difficulty']
                })}
                className="w-full px-3 py-2 border rounded-lg"
                required
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration (days)
              </label>
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  duration: parseInt(e.target.value) 
                })}
                min="1"
                max="90"
                className="w-full px-3 py-2 border rounded-lg"
                required
              />
            </div>
          </div>

          {/* Requirements Section */}
          <div className="space-y-4">
            <h3 className="font-medium">Requirements</h3>

            {/* Flour Requirements */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Required Flour Types
              </label>
              {formData.requirements.flour.map((flour, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={flour}
                    onChange={(e) => updateArrayItem('flour', index, e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg"
                    placeholder="e.g., Whole Wheat Flour"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem('flour', index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('flour')}
                className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Flour Type
              </button>
            </div>

            {/* Equipment Requirements */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Required Equipment
              </label>
              {formData.requirements.equipment.map((equipment, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={equipment}
                    onChange={(e) => updateArrayItem('equipment', index, e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg"
                    placeholder="e.g., Dutch Oven"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem('equipment', index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('equipment')}
                className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Equipment
              </button>
            </div>

            {/* Technique Requirements */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Required Techniques
              </label>
              {formData.requirements.technique.map((technique, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={technique}
                    onChange={(e) => updateArrayItem('technique', index, e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg"
                    placeholder="e.g., Stretch and Fold"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem('technique', index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash className="w-5 h-5" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('technique')}
                className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                Add Technique
              </button>
            </div>
          </div>

          {/* Steps Section */}
          <div className="space-y-4">
            <h3 className="font-medium">Challenge Steps</h3>
            {formData.steps.map((step, index) => (
              <div key={step.id} className="p-4 bg-gray-50 rounded-lg space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">Step {index + 1}</h4>
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => removeStep(index)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash className="w-5 h-5" />
                    </button>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={step.title}
                    onChange={(e) => updateStep(index, 'title', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={step.description}
                    onChange={(e) => updateStep(index, 'description', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg h-24"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tips
                  </label>
                  {step.tips.map((tip, tipIndex) => (
                    <div key={tipIndex} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={tip}
                        onChange={(e) => {
                          const newTips = [...step.tips];
                          newTips[tipIndex] = e.target.value;
                          updateStep(index, 'tips', newTips);
                        }}
                        className="flex-1 px-3 py-2 border rounded-lg"
                        placeholder="Add a helpful tip"
                        required
                      />
                      {tipIndex > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newTips = step.tips.filter((_, i) => i !== tipIndex);
                            updateStep(index, 'tips', newTips);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      const newTips = [...step.tips, ''];
                      updateStep(index, 'tips', newTips);
                    }}
                    className="text-sm text-amber-600 hover:text-amber-700 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" />
                    Add Tip
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addStep}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-amber-600 hover:text-amber-600 flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Add Step
            </button>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border rounded-lg hover:bg-gray-100"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !selectedImage}
              className={`px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-2`}
            >
              {loading && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              )}
              {loading ? 'Creating...' : 'Create Challenge'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}